/**
 * localStorage 过期工具函数
 * 支持设置和获取带过期时间的 localStorage 数据
 */

/**
 * 设置带过期时间的 localStorage
 * @param {string} key - 存储的键名
 * @param {any} value - 存储的值（会自动 JSON.stringify）
 * @param {number} expireTime - 过期时间（毫秒数），例如：1小时 = 3600000
 * @example
 * setItemWithExpiry('myKey', 'true', 3600000) // 1小时后过期
 * setItemWithExpiry('myKey', { data: 'test' }, 24 * 60 * 60 * 1000) // 24小时后过期
 */
export function setItemWithExpiry(key, value, expireTime) {
  const now = new Date();
  const item = {
    value: value,
    expiry: now.getTime() + expireTime
  };
  localStorage.setItem(key, JSON.stringify(item));
}

/**
 * 获取带过期时间的 localStorage
 * @param {string} key - 存储的键名
 * @returns {any|null} 如果未过期返回存储的值，如果过期或不存在返回 null
 * @example
 * const value = getItemWithExpiry('myKey');
 * if (value !== null) {
 *   // 数据有效，可以使用
 * } else {
 *   // 数据已过期或不存在
 * }
 */
export function getItemWithExpiry(key) {
  const itemStr = localStorage.getItem(key);
  
  // 如果不存在，返回 null
  if (!itemStr) {
    return null;
  }

  try {
    const item = JSON.parse(itemStr);
    const now = new Date();

    // 检查是否过期
    if (now.getTime() > item.expiry) {
      // 已过期，删除该项
      localStorage.removeItem(key);
      return null;
    }

    // 未过期，返回存储的值
    return item.value;
  } catch (e) {
    // 解析失败，可能是旧格式的数据，删除它
    localStorage.removeItem(key);
    return null;
  }
}

/**
 * 检查 localStorage 项是否过期（不删除）
 * @param {string} key - 存储的键名
 * @returns {boolean} true 表示未过期，false 表示已过期或不存在
 */
export function isItemValid(key) {
  const itemStr = localStorage.getItem(key);
  if (!itemStr) {
    return false;
  }

  try {
    const item = JSON.parse(itemStr);
    const now = new Date();
    return now.getTime() <= item.expiry;
  } catch (e) {
    return false;
  }
}

/**
 * 删除带过期时间的 localStorage 项
 * @param {string} key - 存储的键名
 */
export function removeItemWithExpiry(key) {
  localStorage.removeItem(key);
}
